import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

import { selectThemeMode } from '../../../store/themes/themes.selectors';

import { TableDataCell } from '../stock-cover/sku-table.styles';
import {
    addOpacity,
    GLOBAL_COLOURS,
} from '../../../utils/styles/global-styles';

function darkModeColorMap(backendColor) {
    const map = {
        '#b5dead': GLOBAL_COLOURS['accents']['emerald-dark'],
        '#fafcb1': GLOBAL_COLOURS['accents']['amber-dark'],
        '#fcd060': GLOBAL_COLOURS['accents']['slate-dark'],
        '#ffc9c9': GLOBAL_COLOURS['accents']['rose-dark'],
    };
    return addOpacity(map[backendColor], 0.7);
}

function mapHeadingToLabel(heading) {
    const map = {
        supplSkuDescr: 'SKU',
        category: 'Category',
        avgDistribution: 'Average Distribution (12mm)',
        ros: 'Rate of Sale',
        quadrant: 'Quadrant',
        quadrantColor: null,

        graphPlot: null,
        strategy: null,
        supplSku: null,
    };
    return map[heading];
}

const DistributionSkuTable = ({ skuData, handleSkuClick, focusSku }) => {
    const tableHeadings = Object.keys(skuData[0]);

    const [filter, setFilter] = useState('');
    const [filteredSkus, setFilteredSkus] = useState(skuData);

    const handleFilterChange = (event) => {
        setFilter(event.currentTarget.value);
        filterSkus(event.currentTarget.value);
    };

    useEffect(() => {
        filterSkus(filter);
    }, [focusSku]);

    const filterSkus = (filter) => {
        const initiallyFiltered = skuData.filter((sku) => {
            return sku.supplSkuDescr
                .toLowerCase()
                .includes(filter.toLowerCase());
        });

        if (focusSku) {
            let shifted = [];

            initiallyFiltered.forEach((sku) => {
                if (sku.supplSku === focusSku.supplSku) shifted.unshift(sku);
                else shifted.push(sku);
            });
            setFilteredSkus(shifted);
        } else {
            setFilteredSkus(initiallyFiltered);
        }
    };

    return (
        <div className="w-full block max-h-screen overflow-y-scroll">
            <div className="w-full min-w-full flex justify-center items-center mb-2 mt-2">
                <MagnifyingGlassIcon className="w-5 h-5 text-primary mr-2" />
                <input
                    className="w-full input input-bordered input-sm max-w-3xl"
                    onChange={handleFilterChange}
                    name="filter"
                    id="filter"
                    type="text"
                />
            </div>

            <table className="table table-compact normal-case w-full">
                <thead>
                    <tr>
                        {tableHeadings.map((heading, index) => {
                            const label = mapHeadingToLabel(heading);
                            if (label) {
                                return (
                                    <th
                                        key={heading}
                                        className={`normal-case text-lg font-light ${
                                            label === 'Category' ||
                                            label === 'SKU' ||
                                            label === 'Quadrant'
                                                ? 'text-left'
                                                : 'text-right'
                                        }`}
                                    >
                                        {label}
                                    </th>
                                );
                            }
                        })}
                    </tr>
                </thead>

                <tbody>
                    {filteredSkus.map((skuObject, index) => {
                        let border;

                        if (
                            focusSku &&
                            focusSku['supplSku'] === skuObject['supplSku']
                        ) {
                            border = 'border-l-4 border-r-4 border-primary';
                        } else {
                            border =
                                'border-l-4 border-r-4 hover:border-primary';
                        }

                        return (
                            <tr
                                key={skuObject['supplSku']}
                                onClick={() => handleSkuClick(skuObject)}
                                className={`cursor-pointer ${border}`}
                            >
                                {Object.keys(skuObject).map((key) => {
                                    const label = mapHeadingToLabel(key);
                                    if (label) {
                                        if (key === 'quadrant') {
                                            return (
                                                <TableDataCell
                                                    key={uuidv4()}
                                                    style={{
                                                        backgroundColor:
                                                            darkModeColorMap(
                                                                skuObject[
                                                                    `${key}Color`
                                                                ]
                                                            ),
                                                    }}
                                                    className={
                                                        isNaN(skuObject[key])
                                                            ? 'text-left'
                                                            : 'text-right'
                                                    }
                                                >
                                                    {skuObject[key]}
                                                </TableDataCell>
                                            );
                                        } else {
                                            return (
                                                <TableDataCell
                                                    key={uuidv4()}
                                                    className={
                                                        isNaN(skuObject[key])
                                                            ? 'text-left'
                                                            : 'text-right'
                                                    }
                                                >
                                                    {skuObject[key]}
                                                </TableDataCell>
                                            );
                                        }
                                    }
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default DistributionSkuTable;